import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { API_BASE } from '../config';
import { useTranslation } from '../translations';
import { DEMO_DISABLED_TITLE } from '../utils/demoBanner';
import { scrollToTop } from '../utils/scrollToTop';
import { slideIn, panelTopHairline, LinkButton } from '../ui/common';
import { reminderSaysTomorrow } from '../utils/notificationHints';
import { RODZAJE_MAILI_WIDGETU, szablonyDoFormularza, maWlasnyTekst } from '../utils/szablonyMailiWidgetu';
import { maPremium, superAdminFirmyBezPremium } from '../utils/planFirmy';
import PremiumNotice from '../components/premium/PremiumNotice';
import PremiumBadge from '../components/premium/PremiumBadge';
import { usePremiumBlad } from '../components/premium/PremiumUpgrade';
import { TylkoOdczytWPrzerwie } from '../components/przerwa/KontoWPrzerwie';

// ── Ikony ──────────────────────────────────────────────────────────────────
const BellIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
);
const MailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
  </svg>
);
const SmsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);
const SaveIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
    <polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
  </svg>
);
const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);
const InfoIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
  </svg>
);

// ── Styled Components ──────────────────────────────────────────────────────
const Container = styled.div`
  max-width: 100%;
  width: 100%;
  margin: 0 auto;
  padding: clamp(12px, 2vw, 24px);
  background: var(--panel-bg);
  border-radius: 16px;
  box-shadow: 0 8px 32px var(--shadow-color);
  color: var(--text);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  box-sizing: border-box;
  min-height: calc(100vh - 48px);
  /* Reszta panelu (18 stron przez PanelContainer, ui/common.js) dostaje przy wejsciu na
     strone delikatny fade+wjazd z dolu. Ta strona miala WLASNY, niezalezny Container
     bez tej animacji — wejscie na nia bylo jedynym miejscem bez zadnego przejscia. */
  animation: ${slideIn} 0.6s ease-out;
  position: relative;
  ${panelTopHairline}
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 32px;
  padding-top: 8px;
`;
const Title = styled.h1`
  margin: 0 0 8px 0;
  font-size: 2rem;
  font-weight: 800;
  color: var(--text);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;
const TitleIcon = styled.div`color: var(--primary); display: flex;`;
const Subtitle = styled.p`margin: 0; font-size: 1rem; color: var(--text-muted);`;

const Card = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 16px;
  padding: clamp(16px, 4vw, 28px);
  margin-bottom: clamp(16px, 3vw, 24px);
  box-shadow: 0 2px 8px var(--shadow-color);
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--input-border);
  margin-bottom: 20px;
`;
const CardTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text);
  flex: 1;
`;
const CardBadge = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 999px;
  background: ${p => p.active ? 'var(--success-bg)' : 'var(--input-border)'};
  color: ${p => p.active ? 'var(--success)' : 'var(--text-muted)'};
  border: 1px solid ${p => p.active ? 'var(--success-border)' : 'var(--input-border)'};
`;

const NotifRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 0;
  border-bottom: 1px solid var(--input-border);
  &:last-child { border-bottom: none; padding-bottom: 0; }
  &:first-child { padding-top: 0; }
`;

const RowTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const Toggle = styled.button`
  position: relative;
  width: 44px;
  height: 24px;
  border-radius: 999px;
  border: none;
  background: ${p => p.on ? 'var(--primary)' : 'var(--input-border)'};
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;
  margin-top: 2px;
  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${p => p.on ? '23px' : '3px'};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    /* Galka przelacznika: swiadomie zawsze biala (konwencja fizycznego przelacznika),
       niezaleznie od trybu — tak samo jak w EmailSettings.js. */
    background: #fff;
    transition: left 0.2s;
    box-shadow: var(--shadow-sm);
  }
`;

const RowInfo = styled.div`flex: 1;`;
const RowLabel = styled.div`font-weight: 600; font-size: 0.95rem; color: var(--text); margin-bottom: 3px;`;
const RowDesc  = styled.div`font-size: 0.84rem; color: var(--text-muted); line-height: 1.4;`;

const ExpandedFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: var(--panel-bg);
  border-radius: 10px;
  border: 1px solid var(--input-border);
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: ${p => p.withDays ? '1fr auto' : '1fr'};
  gap: 12px;
  align-items: end;
  @media (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 5px;
  display: block;
`;

const Input = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid var(--input-border);
  border-radius: 8px;
  background: var(--input-bg);
  color: var(--text);
  font-size: 14px;
  box-sizing: border-box;
  &:focus { outline: none; border-color: var(--primary); box-shadow: var(--focus-ring); }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid var(--input-border);
  border-radius: 8px;
  background: var(--input-bg);
  color: var(--text);
  font-size: 14px;
  font-family: inherit;
  box-sizing: border-box;
  resize: vertical;
  min-height: ${p => p.sms ? '72px' : '110px'};
  line-height: 1.6;
  &:focus { outline: none; border-color: var(--primary); box-shadow: var(--focus-ring); }
`;

const DaysInput = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  font-size: 14px;
  color: var(--text-muted);
  input {
    width: 58px;
    padding: 9px 10px;
    border: 1px solid var(--input-border);
    border-radius: 8px;
    background: var(--input-bg);
    color: var(--text);
    font-size: 14px;
    text-align: center;
    &:focus { outline: none; border-color: var(--primary); box-shadow: var(--focus-ring); }
  }
`;

const VarChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
`;

const VarChip = styled.button`
  font-family: 'Courier New', monospace;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 5px;
  border: 1px solid var(--info-border);
  background: var(--info-bg);
  color: var(--info);
  cursor: pointer;
  &:hover { filter: brightness(0.97); }
`;

const TestButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 7px;
  border: 1px solid var(--input-border);
  background: var(--card-bg);
  color: var(--text-muted);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  &:hover:not(:disabled) { border-color: var(--primary); color: var(--primary); background: var(--info-bg); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const TestResult = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  padding: 6px 10px;
  border-radius: 7px;
  ${p => p.ok
    ? 'color: var(--success); background: var(--success-bg); border: 1px solid var(--success-border);'
    : 'color: var(--danger); background: var(--danger-bg); border: 1px solid var(--danger-border);'
  }
`;

const CharCount = styled.div`
  font-size: 11px;
  color: ${p => p.over ? 'var(--danger)' : 'var(--text-muted)'};
  text-align: right;
`;

const Alert = styled.div`
  padding: 12px 16px;
  border-radius: 10px;
  margin-bottom: 20px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  ${p => p.type === 'success' && 'background:var(--success-bg);border:1px solid var(--success-border);color:var(--success);'}
  ${p => p.type === 'error'   && 'background:var(--danger-bg);border:1px solid var(--danger-border);color:var(--danger);'}
  ${p => p.type === 'info'    && 'background:var(--info-bg);border:1px solid var(--info-border);color:var(--info);'}
`;

const InfoBox = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  background: var(--info-bg);
  border: 1px solid var(--info-border);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: var(--info);
  line-height: 1.5;
  margin-bottom: 16px;
`;

// F.8: przypomnienia i SMS-y bez Premium. `disabled` na fieldsecie wyłącza wszystkie kontrolki naraz.
const PlanLock = styled.fieldset`
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;

  &:disabled {
    opacity: 0.6;
  }

  /* Wiersze w środku fieldsetu liczą :first-child/:last-child względem niego, a nie karty:
     pierwszy tracił odstęp nad sobą, ostatni kreskę pod sobą, i między wierszami robiły się
     dwa paski blisko siebie. Kartę zamyka tylko ostatni wiersz ostatniego fieldsetu. */
  & > ${NotifRow}:first-child { padding-top: 20px; }
  &:not(:last-child) > ${NotifRow}:last-child {
    border-bottom: 1px solid var(--input-border);
    padding-bottom: 20px;
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 22px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: var(--primary);
  color: var(--primary-contrast);
  box-shadow: var(--shadow-primary);
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: var(--shadow-md); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SmsSteps = styled.ol`
  margin: 0 0 16px 0;
  padding-left: 20px;
  font-size: 13px;
  color: var(--text-muted);
  line-height: 1.8;
  a { color: var(--primary); }
`;

// ── Domyślne szablony ──────────────────────────────────────────────────────
const DEFAULTS = {
  email_confirm_subject: 'Potwierdzenie rezerwacji w {{organizationName}}',
  email_confirm_body: `Dzień dobry {{firstName}},

Dziękujemy za rezerwację w {{organizationName}}!

Szczegóły:
Odbiór:  {{startDate}}
Zwrot:   {{endDate}}

W razie pytań prosimy o kontakt: {{organizationPhone}}

Do zobaczenia!
Zespół {{organizationName}}`,

  email_pickup_subject: 'Przypomnienie o odbiorze w {{organizationName}}',
  email_pickup_body: `Dzień dobry {{firstName}},

Przypominamy, że {{pickupReminderDate}} zapraszamy po odbiór sprzętu w {{organizationName}}.

Kontakt: {{organizationPhone}}

Do zobaczenia!
Zespół {{organizationName}}`,

  email_return_subject: 'Przypomnienie o zwrocie w {{organizationName}}',
  email_return_body: `Dzień dobry {{firstName}},

Przypominamy, że {{returnReminderDate}} upływa termin zwrotu sprzętu wypożyczonego w {{organizationName}}.

Kontakt: {{organizationPhone}}

Dziękujemy!
Zespół {{organizationName}}`,

  email_contract_subject: 'Twoja umowa z {{organizationName}} jest gotowa',
  email_contract_body: `Dzień dobry {{firstName}},

Umowa wypożyczenia sprzętu z {{organizationName}} została wygenerowana i przesłana do Ciebie.

Dziękujemy za skorzystanie z naszych usług!
Zespół {{organizationName}}`,

  sms_confirm_body: 'Potw. rezerwacji w {{organizationName}}. Odbior: {{startDate}}, zwrot: {{endDate}}. Kontakt: {{organizationPhone}}',
  // Bez „jutro": liczba dni wyprzedzenia jest ustawiana (migracja 042 zmieniła też DEFAULT w bazie).
  sms_return_body:  '{{organizationName}}: termin zwrotu sprzetu mija {{endDate}}. Kontakt: {{organizationPhone}}',
};

// Mapowanie kod → KLUCZ i18n etykiety. P8.1e: bezpieczne, bo klik wstawia KOD przez
// insertVar(v) — etykieta żyje wyłącznie na chipie i nie trafia do treści szablonu.
const VAR_LABEL_KEYS = {
  '{{firstName}}':          'notifications.varFirstName',
  '{{lastName}}':           'notifications.varLastName',
  '{{startDate}}':          'notifications.varStartDate',
  '{{endDate}}':            'notifications.varEndDate',
  '{{organizationName}}':   'notifications.varOrgName',
  '{{organizationPhone}}':  'notifications.varOrgPhone',
  '{{pickupReminderDate}}': 'notifications.varPickupDate',
  '{{returnReminderDate}}': 'notifications.varReturnDate',
  // Maile z widgetu (073)
  '{{reservationId}}':       'notifications.varReservationId',
  '{{organizationEmail}}':   'notifications.varOrgEmail',
  '{{organizationContact}}': 'notifications.varOrgContact',
};

const EMAIL_VARS = ['{{firstName}}', '{{lastName}}', '{{startDate}}', '{{endDate}}', '{{organizationName}}', '{{organizationPhone}}'];
const PICKUP_VARS = [...EMAIL_VARS, '{{pickupReminderDate}}'];
const RETURN_VARS = [...EMAIL_VARS, '{{returnReminderDate}}'];
const SMS_VARS = ['{{firstName}}', '{{startDate}}', '{{endDate}}', '{{organizationName}}', '{{organizationPhone}}'];

// Pod polem tokenu SMSAPI: stan „zapisany" i jego usunięcie (token jest tylko do zapisu).
const TokenNote = styled.div`
  margin-top: var(--sp-2);
  font-size: var(--fs-xs);
  color: var(--text-muted);
`;

// Ostrzeżenie pod szablonem przypomnienia, gdy treść mówi „jutro", a wyprzedzenie jest inne niż 1 dzień.
const ReminderHint = styled.p`
  margin: var(--sp-2) 0 0;
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--warning-border);
  border-radius: var(--r-sm);
  background: var(--warning-bg);
  color: var(--warning);
  font-size: var(--fs-sm);
  line-height: 1.45;
`;

const SendIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
);

// ── Komponent pomocniczy: pole tekstowe + wstawianie zmiennych ─────────────
function TemplateField({ label, value, onChange, vars = EMAIL_VARS, sms = false, maxLen, notifType, onTest, testState, testDisabled = false }) {
  const { t } = useTranslation();
  const len = (value || '').length;
  const textareaRef = useRef(null);
  const cursorPosRef = useRef(null); // ostatnia pozycja kursora (zachowana przy blur)

  const insertVar = (v) => {
    const ta = textareaRef.current;
    const current = value || '';
    // Użyj zapisanej pozycji (po blur) lub aktualnej selectionStart, w ostateczności końca
    const pos = cursorPosRef.current ?? ta?.selectionStart ?? current.length;
    const start = Math.min(pos, current.length);
    const end = Math.min(cursorPosRef.current ?? ta?.selectionEnd ?? start, current.length);
    const next = current.slice(0, start) + v + current.slice(end);
    onChange(next);
    // Ustaw kursor za wstawioną zmienną
    const newPos = start + v.length;
    cursorPosRef.current = newPos;
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        try { textareaRef.current.setSelectionRange(newPos, newPos); } catch (_) {}
      }
    });
  };

  const usedVars = vars.filter(v => (value || '').includes(v));

  return (
    <div>
      <Label>{label}</Label>
      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '6px' }}>
        {t('notifications.insertHint')}
      </div>
      <Textarea
        ref={textareaRef}
        sms={sms}
        value={value}
        onChange={e => { onChange(e.target.value); cursorPosRef.current = e.target.selectionStart; }}
        onSelect={e => { cursorPosRef.current = e.target.selectionStart; }}
        onBlur={e => { cursorPosRef.current = e.target.selectionStart; }}
        maxLength={maxLen}
      />
      {maxLen && <CharCount over={len > maxLen * 0.9}>{len} / {maxLen} {t('notifications.chars')}</CharCount>}
      <div style={{ marginTop: '8px', marginBottom: '4px', fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {t('notifications.insertData')}
      </div>
      <VarChips>
        {vars.map(v => (
          <VarChip
            key={v}
            type="button"
            onClick={() => insertVar(v)}
            title={t('notifications.willInsert', { var: v })}
            style={{ opacity: usedVars.includes(v) ? 1 : 0.7 }}
          >
            {VAR_LABEL_KEYS[v] ? t(VAR_LABEL_KEYS[v]) : v}
          </VarChip>
        ))}
      </VarChips>
      {usedVars.length > 0 && (
        <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.6 }}>
          <span style={{ fontWeight: 600 }}>{t('notifications.usedInBody')} </span>
          {usedVars.map(v => (
            <span key={v} style={{ marginRight: '10px' }}>
              <span style={{ fontFamily: 'monospace', background: 'var(--panel-bg)', padding: '1px 5px', borderRadius: '3px', fontSize: '11px' }}>{v}</span>
              {' = '}{VAR_LABEL_KEYS[v] ? t(VAR_LABEL_KEYS[v]) : v}
            </span>
          ))}
        </div>
      )}
      {notifType && onTest && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '6px', marginTop: '12px' }}>
          <TestButton
            type="button"
            onClick={() => onTest(notifType)}
            disabled={testState === 'sending' || testDisabled}
            title={testDisabled ? DEMO_DISABLED_TITLE : undefined}
          >
            <SendIcon />
            {t('notifications.sendTest')}
          </TestButton>
          {testState === 'sending' && (
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{t('notifications.sending')}</span>
          )}
          {testState && testState !== 'sending' && (
            <TestResult ok={testState.ok}>
              {testState.ok ? '✓' : '✕'} {testState.msg}
            </TestResult>
          )}
        </div>
      )}
    </div>
  );
}

// ── Główny komponent ───────────────────────────────────────────────────────
const EMPTY_SETTINGS = {
  email_confirm_enabled: false,
  email_confirm_subject: DEFAULTS.email_confirm_subject,
  email_confirm_body:    DEFAULTS.email_confirm_body,
  email_pickup_enabled:  false,
  email_pickup_days_before: 1,
  email_pickup_subject:  DEFAULTS.email_pickup_subject,
  email_pickup_body:     DEFAULTS.email_pickup_body,
  email_return_enabled:  false,
  email_return_days_before: 1,
  email_return_subject:  DEFAULTS.email_return_subject,
  email_return_body:     DEFAULTS.email_return_body,
  email_contract_enabled: false,
  email_contract_subject: DEFAULTS.email_contract_subject,
  email_contract_body:    DEFAULTS.email_contract_body,
  sms_enabled:            false,
  smsapi_token:           '',
  // Token jest tylko do zapisu: serwer oddaje wyłącznie informację, czy jest zapisany.
  smsapi_token_set:       false,
  smsapi_token_clear:     false,
  sms_sender_name:        'the.rENT',
  sms_confirm_enabled:    false,
  sms_confirm_body:       DEFAULTS.sms_confirm_body,
  sms_return_enabled:     false,
  sms_return_days_before: 1,
  sms_return_body:        DEFAULTS.sms_return_body,
  // Mail do wypożyczalni o nowej rezerwacji z widgetu (067). Domyślnie włączony; pusty adres =
  // e-mail z Danych firmy, a bez niego adresy administratorów.
  email_staff_widget_enabled: true,
  staff_notification_email:   '',
};

export default function Notifications() {
  const { t } = useTranslation();
  const isDemo = localStorage.getItem('isDemo') === '1';
  // F.8: automatyczne przypomnienia mailem i każdy SMS są w Premium (decyzja Piotrka 2026-10-23):
  // serwer odrzuca ich włączenie 403 i nie wysyła ich z firmy na Basic. Potwierdzenie rezerwacji
  // mailem i umowa zostają w Basic.
  // `planFirmy` z GET /api/notification-settings: aktualny plan, także w karcie sprzed jego zmiany.
  const [planFirmy, setPlanFirmy] = useState(null);
  const bezPremium = !maPremium(planFirmy);
  const superAdminBezPremium = superAdminFirmyBezPremium(planFirmy);
  const [form, setForm]       = useState(EMPTY_SETTINGS);
  const [savedForm, setSavedForm] = useState(EMPTY_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [alert, setAlert]     = useState(null);
  const pokazBladPremium = usePremiumBlad();
  const [testStates, setTestStates] = useState({});
  // 073: teksty domyślne maili z widgetu i ich pola {{…}} z serwera; rozwinięty mail na karcie.
  const [widgetEmailDefaults, setWidgetEmailDefaults] = useState(null);
  const [widgetEmailVars, setWidgetEmailVars] = useState([]);
  const [otwartyMailWidgetu, setOtwartyMailWidgetu] = useState(null);

  const isDirty = JSON.stringify(form) !== JSON.stringify(savedForm);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/notification-settings`, { credentials: 'include' });
        if (!r.ok) throw new Error();
        const data = await r.json();
        if (typeof data.organizationPlan === 'string') setPlanFirmy(data.organizationPlan);
        // 073: teksty maili z widgetu. Bez tekstów domyślnych (starszy serwer) karty nie ma,
        // a zapis nie wysyła tego pola, więc nic nie kasuje.
        const szablonyWidgetu = szablonyDoFormularza(data.widgetEmailDefaults, data.settings && data.settings.widget_email_templates);
        if (szablonyWidgetu) {
          setWidgetEmailDefaults(data.widgetEmailDefaults);
          setWidgetEmailVars(Array.isArray(data.widgetEmailVariables) ? data.widgetEmailVariables.map(v => `{{${v}}}`) : []);
          if (!data.settings) {
            const pusty = { ...EMPTY_SETTINGS, widget_email_templates: szablonyWidgetu };
            setForm(pusty);
            setSavedForm(pusty);
          }
        }
        if (data.settings) {
          const merged = {
            ...EMPTY_SETTINGS,
            ...data.settings,
            email_confirm_subject: data.settings.email_confirm_subject || DEFAULTS.email_confirm_subject,
            email_confirm_body:    data.settings.email_confirm_body    || DEFAULTS.email_confirm_body,
            email_pickup_subject:  data.settings.email_pickup_subject  || DEFAULTS.email_pickup_subject,
            email_pickup_body:     data.settings.email_pickup_body     || DEFAULTS.email_pickup_body,
            email_return_subject:  data.settings.email_return_subject  || DEFAULTS.email_return_subject,
            email_return_body:     data.settings.email_return_body     || DEFAULTS.email_return_body,
            email_contract_subject: data.settings.email_contract_subject || DEFAULTS.email_contract_subject,
            email_contract_body:    data.settings.email_contract_body    || DEFAULTS.email_contract_body,
            sms_confirm_body: data.settings.sms_confirm_body || DEFAULTS.sms_confirm_body,
            sms_return_body:  data.settings.sms_return_body  || DEFAULTS.sms_return_body,
            smsapi_token: '',
            smsapi_token_set: !!data.settings.smsapi_token_set,
            smsapi_token_clear: false,
            sms_sender_name: data.settings.sms_sender_name || 'the.rENT',
            email_staff_widget_enabled: data.settings.email_staff_widget_enabled !== false,
            staff_notification_email: data.settings.staff_notification_email || '',
          };
          delete merged.widget_email_templates;
          if (szablonyWidgetu) merged.widget_email_templates = szablonyWidgetu;
          setForm(merged);
          setSavedForm(merged);
        }
      } catch (_) {
        setAlert({ type: 'error', msg: t('notifications.loadError') });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const handler = (e) => { if (isDirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const set = (field) => (val) => setForm(f => ({ ...f, [field]: val }));
  // Pole jednego maila z widgetu (temat, wstęp albo zakończenie).
  const setMailWidgetu = (rodzaj, pole) => (val) => setForm(f => ({
    ...f,
    widget_email_templates: {
      ...f.widget_email_templates,
      [rodzaj]: { ...f.widget_email_templates[rodzaj], [pole]: val },
    },
  }));
  const przywrocMailWidgetu = (rodzaj) => setForm(f => ({
    ...f,
    widget_email_templates: { ...f.widget_email_templates, [rodzaj]: { ...widgetEmailDefaults[rodzaj] } },
  }));
  const setVal = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  const toggle = (field) => () => setForm(f => ({ ...f, [field]: !f[field] }));

  const handleSave = async () => {
    setSaving(true);
    setAlert(null);
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const r = await fetch(`${API_BASE}/api/notification-settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok && pokazBladPremium(data)) return;
      if (!r.ok) throw new Error(data.error || t('notifications.saveError'));
      // Po zapisie pole tokenu wraca do pustego, a to, czy token jest zapisany, mówi serwer.
      const next = { ...form, smsapi_token: '', smsapi_token_clear: false, smsapi_token_set: !!data.smsapi_token_set };
      setForm(next);
      setSavedForm(next);
      setAlert({ type: 'success', msg: t('notifications.saved') });
      setTimeout(() => setAlert(null), 4000);
    } catch (e) {
      setAlert({ type: 'error', msg: e.message || t('notifications.saveGenericError') });
    } finally {
      setSaving(false);
      scrollToTop();
    }
  };

  const handleTest = async (type) => {
    setTestStates(s => ({ ...s, [type]: 'sending' }));
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const r = await fetch(`${API_BASE}/api/notification-settings/test`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
        body: JSON.stringify({ type }),
      });
      const data = await r.json();
      if (!r.ok && pokazBladPremium(data)) { setTestStates(s => ({ ...s, [type]: null })); return; }
      if (!r.ok) throw new Error(data.error || t('notifications.sendError'));
      setTestStates(s => ({ ...s, [type]: { ok: true, msg: data.message } }));
    } catch (e) {
      setTestStates(s => ({ ...s, [type]: { ok: false, msg: e.message } }));
    }
    setTimeout(() => setTestStates(s => ({ ...s, [type]: null })), 6000);
  };

  const anyEmailActive = form.email_confirm_enabled || form.email_pickup_enabled || form.email_return_enabled || form.email_contract_enabled;
  const anySmsActive   = form.sms_enabled && (form.sms_confirm_enabled || form.sms_return_enabled);

  if (loading) return (
    <Container>
      <Header><Title><TitleIcon><BellIcon /></TitleIcon>Powiadomienia</Title></Header>
      <p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{t('notifications.loading')}</p>
    </Container>
  );

  return (
    <Container>
      <Header>
        <Title><TitleIcon><BellIcon /></TitleIcon>{t('notifications.title')}</Title>
        <Subtitle>{t('notifications.subtitle')}</Subtitle>
      </Header>
      <TylkoOdczytWPrzerwie>

      {isDemo && (
        <Alert type="info">
          <InfoIcon /> {t('notifications.demoNotice')}
        </Alert>
      )}

      {bezPremium && (
        <PremiumNotice
          feature="reminders"
          title={t('notifications.premiumTitle')}
          style={{ marginBottom: 20 }}
          data-cy="notifications-premium"
        >
          {t('notifications.premiumBody')}
        </PremiumNotice>
      )}

      {superAdminBezPremium && (
        <Alert type="info" data-cy="notifications-superadmin-plan">
          <InfoIcon />
          <span>{t('notifications.superAdminNotPremium')}</span>
        </Alert>
      )}

      {alert && (
        <Alert type={alert.type}>
          {alert.type === 'success' ? <CheckIcon /> : <InfoIcon />}
          {alert.msg}
        </Alert>
      )}

      {/* ── DLA WYPOŻYCZALNI (067): mail o każdej nowej rezerwacji z widgetu ── */}
      <Card data-cy="notifications-staff">
        <CardHeader>
          <MailIcon />
          <CardTitle>{t('notifications.staffCard')}</CardTitle>
          <CardBadge active={form.email_staff_widget_enabled}>{form.email_staff_widget_enabled ? t('notifications.active') : t('notifications.inactive')}</CardBadge>
        </CardHeader>
        <NotifRow>
          <RowTop>
            <Toggle
              type="button"
              role="switch"
              aria-checked={form.email_staff_widget_enabled}
              on={form.email_staff_widget_enabled}
              onClick={toggle('email_staff_widget_enabled')}
              aria-label={t('notifications.staffWidgetLabel')}
              title={t('notifications.staffWidgetLabel')}
              data-cy="notifications-staff-toggle"
            />
            <RowInfo>
              <RowLabel>{t('notifications.staffWidgetLabel')}</RowLabel>
              <RowDesc>{t('notifications.staffWidgetDesc')}</RowDesc>
            </RowInfo>
          </RowTop>
          {form.email_staff_widget_enabled && (
            <ExpandedFields>
              <div>
                <Label htmlFor="notifications-staff-email">{t('notifications.staffEmailLabel')}</Label>
                <Input
                  id="notifications-staff-email"
                  type="email"
                  value={form.staff_notification_email}
                  onChange={setVal('staff_notification_email')}
                  placeholder={t('notifications.staffEmailPlaceholder')}
                  maxLength={254}
                  data-cy="notifications-staff-email"
                />
                <RowDesc style={{ marginTop: 6 }}>{t('notifications.staffEmailHint')}</RowDesc>
              </div>
            </ExpandedFields>
          )}
        </NotifRow>
      </Card>

      {/* ── EMAIL ── */}
      <Card>
        <CardHeader data-tour="notifications-email">
          <MailIcon />
          <CardTitle>{t('notifications.emailCard')}</CardTitle>
          <CardBadge active={anyEmailActive}>{anyEmailActive ? t('notifications.active') : t('notifications.inactive')}</CardBadge>
        </CardHeader>

        <InfoBox data-cy="notifications-email-sender-info">
          <InfoIcon />
          <span>{t('notifications.smtpInfoBefore')} <strong>{t('emailSettings.title')}</strong>{t('notifications.smtpInfoAfter')}</span>
        </InfoBox>

        <NotifRow>
          <RowTop>
            <Toggle type="button" role="switch" aria-checked={form.email_confirm_enabled} on={form.email_confirm_enabled} onClick={toggle('email_confirm_enabled')} aria-label={t('notifications.confirmLabel')} title={t('notifications.confirmLabel')} data-cy="notifications-confirm-toggle" />
            <RowInfo>
              <RowLabel>{t('notifications.confirmLabel')}</RowLabel>
              <RowDesc>{t('notifications.confirmDesc')}</RowDesc>
            </RowInfo>
          </RowTop>
          {form.email_confirm_enabled && (
            <ExpandedFields>
              <div>
                <Label>{t('notifications.subject')}</Label>
                <Input value={form.email_confirm_subject} onChange={setVal('email_confirm_subject')} maxLength={200} />
              </div>
              <TemplateField label={t('notifications.body')} value={form.email_confirm_body} onChange={set('email_confirm_body')} vars={EMAIL_VARS} maxLen={2000} notifType="confirm" onTest={handleTest} testState={testStates['confirm']} />
            </ExpandedFields>
          )}
        </NotifRow>

        <PlanLock disabled={bezPremium} data-cy="notifications-reminders-lock">
        {bezPremium && (
          <InfoBox>
            <PremiumBadge />
            <span>{t('notifications.remindersPremium')}</span>
          </InfoBox>
        )}
        <NotifRow>
          <RowTop>
            <Toggle type="button" role="switch" aria-checked={form.email_pickup_enabled} on={form.email_pickup_enabled} onClick={toggle('email_pickup_enabled')} aria-label={t('notifications.pickupLabel')} title={t('notifications.pickupLabel')} />
            <RowInfo>
              <RowLabel>{t('notifications.pickupLabel')}</RowLabel>
              <RowDesc>{t('notifications.pickupDesc')}</RowDesc>
            </RowInfo>
          </RowTop>
          {form.email_pickup_enabled && (
            <ExpandedFields>
              <FieldRow withDays>
                <div>
                  <Label>{t('notifications.subject')}</Label>
                  <Input value={form.email_pickup_subject} onChange={setVal('email_pickup_subject')} maxLength={200} />
                </div>
                <DaysInput>
                  <input
                    type="number" min="1" max="30"
                    value={form.email_pickup_days_before}
                    onChange={e => setForm(f => ({ ...f, email_pickup_days_before: Math.max(1, Math.min(30, parseInt(e.target.value) || 1)) }))}
                  />
                  <span>{t('notifications.daysBefore')}</span>
                </DaysInput>
              </FieldRow>
              <TemplateField label={t('notifications.body')} value={form.email_pickup_body} onChange={set('email_pickup_body')} vars={PICKUP_VARS} maxLen={2000} notifType="pickup" onTest={handleTest} testState={testStates['pickup']} />
              {reminderSaysTomorrow(`${form.email_pickup_subject}\n${form.email_pickup_body}`, form.email_pickup_days_before) && (
                <ReminderHint role="status">{t('notifications.tomorrowMismatch')}</ReminderHint>
              )}
            </ExpandedFields>
          )}
        </NotifRow>

        <NotifRow>
          <RowTop>
            <Toggle type="button" role="switch" aria-checked={form.email_return_enabled} on={form.email_return_enabled} onClick={toggle('email_return_enabled')} aria-label={t('notifications.returnLabel')} title={t('notifications.returnLabel')} />
            <RowInfo>
              <RowLabel>{t('notifications.returnLabel')}</RowLabel>
              <RowDesc>{t('notifications.returnDesc')}</RowDesc>
            </RowInfo>
          </RowTop>
          {form.email_return_enabled && (
            <ExpandedFields>
              <FieldRow withDays>
                <div>
                  <Label>{t('notifications.subject')}</Label>
                  <Input value={form.email_return_subject} onChange={setVal('email_return_subject')} maxLength={200} />
                </div>
                <DaysInput>
                  <input
                    type="number" min="1" max="30"
                    value={form.email_return_days_before}
                    onChange={e => setForm(f => ({ ...f, email_return_days_before: Math.max(1, Math.min(30, parseInt(e.target.value) || 1)) }))}
                  />
                  <span>{t('notifications.daysBefore')}</span>
                </DaysInput>
              </FieldRow>
              <TemplateField label={t('notifications.body')} value={form.email_return_body} onChange={set('email_return_body')} vars={RETURN_VARS} maxLen={2000} notifType="return" onTest={handleTest} testState={testStates['return']} />
              {reminderSaysTomorrow(`${form.email_return_subject}\n${form.email_return_body}`, form.email_return_days_before) && (
                <ReminderHint role="status">{t('notifications.tomorrowMismatch')}</ReminderHint>
              )}
            </ExpandedFields>
          )}
        </NotifRow>

        </PlanLock>

        <NotifRow>
          <RowTop>
            <Toggle type="button" role="switch" aria-checked={form.email_contract_enabled} on={form.email_contract_enabled} onClick={toggle('email_contract_enabled')} aria-label={t('notifications.contractLabel')} title={t('notifications.contractLabel')} />
            <RowInfo>
              <RowLabel>{t('notifications.contractLabel')}</RowLabel>
              <RowDesc>{t('notifications.contractDesc')}</RowDesc>
            </RowInfo>
          </RowTop>
          {form.email_contract_enabled && (
            <ExpandedFields>
              <div>
                <Label>{t('notifications.subject')}</Label>
                <Input value={form.email_contract_subject} onChange={setVal('email_contract_subject')} maxLength={200} />
              </div>
              <TemplateField label={t('notifications.body')} value={form.email_contract_body} onChange={set('email_contract_body')} vars={EMAIL_VARS} maxLen={2000} notifType="contract" onTest={handleTest} testState={testStates['contract']} />
            </ExpandedFields>
          )}
        </NotifRow>
      </Card>

      {/* ── MAILE Z WIDGETU (073): temat, wstęp i zakończenie; środek maila składa system ── */}
      {widgetEmailDefaults && form.widget_email_templates && (
        <Card data-cy="notifications-widget-emails">
          <CardHeader>
            <MailIcon />
            <CardTitle>{t('notifications.widgetEmailsCard')}</CardTitle>
          </CardHeader>
          <InfoBox>
            <InfoIcon />
            <span>{t('notifications.widgetEmailsInfo')}</span>
          </InfoBox>
          {RODZAJE_MAILI_WIDGETU.map(rodzaj => {
            const szablon = form.widget_email_templates[rodzaj];
            const otwarty = otwartyMailWidgetu === rodzaj;
            const wlasny = maWlasnyTekst(form.widget_email_templates, widgetEmailDefaults, rodzaj);
            return (
              <NotifRow key={rodzaj} data-cy={`notifications-widget-email-${rodzaj}`}>
                <RowTop>
                  <RowInfo>
                    <RowLabel>
                      {t(`notifications.widgetEmail.${rodzaj}.label`)}{' '}
                      <CardBadge active={wlasny}>{wlasny ? t('notifications.widgetEmailCustom') : t('notifications.widgetEmailDefault')}</CardBadge>
                    </RowLabel>
                    <RowDesc>{t(`notifications.widgetEmail.${rodzaj}.desc`)}</RowDesc>
                  </RowInfo>
                  <LinkButton
                    type="button"
                    aria-expanded={otwarty}
                    onClick={() => setOtwartyMailWidgetu(otwarty ? null : rodzaj)}
                    data-cy={`notifications-widget-email-edit-${rodzaj}`}
                  >
                    {otwarty ? t('notifications.widgetEmailCollapse') : t('notifications.widgetEmailEdit')}
                  </LinkButton>
                </RowTop>
                {otwarty && (
                  <ExpandedFields>
                    <div>
                      <Label>{t('notifications.subject')}</Label>
                      <Input value={szablon.subject} onChange={e => setMailWidgetu(rodzaj, 'subject')(e.target.value)} maxLength={200} data-cy={`notifications-widget-email-subject-${rodzaj}`} />
                    </div>
                    <TemplateField label={t('notifications.widgetEmailIntro')} value={szablon.intro} onChange={setMailWidgetu(rodzaj, 'intro')} vars={widgetEmailVars} maxLen={2000} />
                    <InfoBox>
                      <InfoIcon />
                      <span><strong>{t('notifications.widgetEmailSystemPart')}</strong> {t(`notifications.widgetEmail.${rodzaj}.system`)}</span>
                    </InfoBox>
                    <TemplateField label={t('notifications.widgetEmailOutro')} value={szablon.outro} onChange={setMailWidgetu(rodzaj, 'outro')} vars={widgetEmailVars} maxLen={2000} />
                    {wlasny && (
                      <div>
                        <LinkButton type="button" onClick={() => przywrocMailWidgetu(rodzaj)} data-cy={`notifications-widget-email-reset-${rodzaj}`}>
                          {t('notifications.widgetEmailReset')}
                        </LinkButton>
                      </div>
                    )}
                  </ExpandedFields>
                )}
              </NotifRow>
            );
          })}
        </Card>
      )}

      {/* ── SMS ── */}
      <Card>
        <CardHeader data-tour="notifications-sms">
          <SmsIcon />
          <CardTitle>{t('notifications.smsCard')}</CardTitle>
          <CardBadge active={anySmsActive}>{anySmsActive ? t('notifications.active') : t('notifications.inactive')}</CardBadge>
        </CardHeader>

        <InfoBox>
          <InfoIcon />
          <span>{t('notifications.smsInfoBefore')} <strong>SMSAPI.pl</strong>{t('notifications.smsInfoAfter')}</span>
        </InfoBox>

        {bezPremium && (
          <InfoBox data-cy="notifications-sms-premium">
            <PremiumBadge />
            <span>{t('notifications.smsPremium')}</span>
          </InfoBox>
        )}

        {/* Bez Premium zapisany token SMSAPI wolno tylko usunąć (serwer na to pozwala), więc ten
            przycisk stoi poza zablokowaną kartą. */}
        {bezPremium && form.smsapi_token_set && (
          <TokenNote data-cy="notifications-sms-token-remove">
            {form.smsapi_token_clear ? (
              <>
                {t('notifications.smsTokenWillRemove')}{' '}
                <LinkButton onClick={() => setForm(f => ({ ...f, smsapi_token_clear: false }))}>
                  {t('common.cancel')}
                </LinkButton>
              </>
            ) : (
              <LinkButton $tone="danger" onClick={() => setForm(f => ({ ...f, smsapi_token_clear: true }))}>
                {t('notifications.smsTokenRemove')}
              </LinkButton>
            )}
          </TokenNote>
        )}

        <PlanLock disabled={bezPremium} data-cy="notifications-sms-lock">
        <NotifRow>
          <RowTop>
            <Toggle type="button" role="switch" aria-checked={form.sms_enabled} on={form.sms_enabled} onClick={toggle('sms_enabled')} aria-label={t('notifications.smsEnableLabel')} title={t('notifications.smsEnableLabel')} />
            <RowInfo>
              <RowLabel>{t('notifications.smsEnableLabel')}</RowLabel>
              <RowDesc>{t('notifications.smsEnableDesc')}</RowDesc>
            </RowInfo>
          </RowTop>
          {form.sms_enabled && (
            <ExpandedFields>
              <SmsSteps>
                <li>{t('notifications.smsStep1a')} <a href="https://smsapi.pl" target="_blank" rel="noopener noreferrer">smsapi.pl</a> {t('notifications.smsStep1b')}</li>
                <li>{t('notifications.smsStep2a')} <strong>{t('notifications.smsStep2b')}</strong> {t('notifications.smsStep2c')}</li>
                <li>{t('notifications.smsStep3')}</li>
              </SmsSteps>
              <FieldRow>
                <div>
                  <Label>{t('notifications.smsToken')}</Label>
                  <Input
                    type="password"
                    value={form.smsapi_token}
                    onChange={setVal('smsapi_token')}
                    placeholder={form.smsapi_token_set && !form.smsapi_token_clear ? t('notifications.smsTokenSavedPh') : t('notifications.smsTokenPh')}
                    autoComplete="off"
                    disabled={form.smsapi_token_clear}
                    data-cy="notifications-sms-token"
                  />
                  {!bezPremium && form.smsapi_token_set && !form.smsapi_token && (
                    <TokenNote>
                      {form.smsapi_token_clear ? (
                        <>
                          {t('notifications.smsTokenWillRemove')}{' '}
                          <LinkButton onClick={() => setForm(f => ({ ...f, smsapi_token_clear: false }))}>
                            {t('common.cancel')}
                          </LinkButton>
                        </>
                      ) : (
                        <LinkButton $tone="danger" onClick={() => setForm(f => ({ ...f, smsapi_token_clear: true }))}>
                          {t('notifications.smsTokenRemove')}
                        </LinkButton>
                      )}
                    </TokenNote>
                  )}
                </div>
              </FieldRow>
              <FieldRow>
                <div>
                  <Label>{t('notifications.smsSender')}</Label>
                  <Input
                    value={form.sms_sender_name}
                    onChange={e => setForm(f => ({ ...f, sms_sender_name: e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 11) }))}
                    placeholder={t('notifications.smsSenderPh')}
                    maxLength={11}
                  />
                </div>
              </FieldRow>
            </ExpandedFields>
          )}
        </NotifRow>

        {form.sms_enabled && (
          <>
            <NotifRow>
              <RowTop>
                <Toggle type="button" role="switch" aria-checked={form.sms_confirm_enabled} on={form.sms_confirm_enabled} onClick={toggle('sms_confirm_enabled')} aria-label={t('notifications.smsConfirmLabel')} title={t('notifications.smsConfirmLabel')} />
                <RowInfo>
                  <RowLabel>{t('notifications.smsConfirmLabel')}</RowLabel>
                  <RowDesc>{t('notifications.smsConfirmDesc')}</RowDesc>
                </RowInfo>
              </RowTop>
              {form.sms_confirm_enabled && (
                <ExpandedFields>
                  <TemplateField label={t('notifications.smsBody')} value={form.sms_confirm_body} onChange={set('sms_confirm_body')} vars={SMS_VARS} sms maxLen={160} />
                </ExpandedFields>
              )}
            </NotifRow>

            <NotifRow>
              <RowTop>
                <Toggle type="button" role="switch" aria-checked={form.sms_return_enabled} on={form.sms_return_enabled} onClick={toggle('sms_return_enabled')} aria-label={t('notifications.smsReturnLabel')} title={t('notifications.smsReturnLabel')} />
                <RowInfo>
                  <RowLabel>{t('notifications.smsReturnLabel')}</RowLabel>
                  <RowDesc>{t('notifications.smsReturnDesc')}</RowDesc>
                </RowInfo>
              </RowTop>
              {form.sms_return_enabled && (
                <ExpandedFields>
                  <FieldRow withDays>
                    <div style={{ flex: 1 }}>
                      <TemplateField label={t('notifications.smsBody')} value={form.sms_return_body} onChange={set('sms_return_body')} vars={SMS_VARS} sms maxLen={160} />
                      {reminderSaysTomorrow(form.sms_return_body, form.sms_return_days_before) && (
                        <ReminderHint role="status">{t('notifications.tomorrowMismatch')}</ReminderHint>
                      )}
                    </div>
                    <DaysInput style={{ alignSelf: 'flex-start', marginTop: '22px' }}>
                      <input
                        type="number" min="1" max="30"
                        value={form.sms_return_days_before}
                        onChange={e => setForm(f => ({ ...f, sms_return_days_before: Math.max(1, Math.min(30, parseInt(e.target.value) || 1)) }))}
                      />
                      <span>{t('notifications.daysBefore')}</span>
                    </DaysInput>
                  </FieldRow>
                </ExpandedFields>
              )}
            </NotifRow>
          </>
        )}
        </PlanLock>
      </Card>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={handleSave} disabled={saving || !isDirty}>
          <SaveIcon />
          {saving ? t('notifications.saving') : t('notifications.save')}
        </Button>
      </div>
      </TylkoOdczytWPrzerwie>
    </Container>
  );
}
